import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType, RadioButtonWithLabelGroupWidget, RadioLayoutType, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { AuthenticationTypeResolutionService } from '~src/authentication/services/AuthenticationTypeResolutionService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { Messages } from '../../../codegen/Localize';
import { WindowLocationService } from '../../../commonInfra/services/WindowLocationService';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { FileInput, FileInputProps } from '../../../userManagement/components/FileInput';
import { EmailValidationHelper } from '../../../userManagement/helpers/EmailValidationHelper';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import { AuthenticationFileInput } from '../components/AuthenticationFileInput';
import {
    IIdPMetadataFields,
    ISAMLAuthenticationProvider,
    SiteAuthenticationSettingsService
} from '../services/SiteAuthenticationSettingsService';
import {
    emailMappingTextBoxStyle,
    samlDisabled,
    samlProviderAlertText,
    samlProviderDarkHighlightTextStyle,
    samlProviderExportMetadataLabelStyle,
    samlProviderExportMetadataTextInput,
    samlProviderGroupButton,
    samlProviderGroupContent,
    samlProviderGroupNumber,
    samlProviderGroupSettings,
    samlProviderImportMetadataTextInput,
    samlProviderListStyle,
    samlProviderMatchAttributesTitleStyle,
    samlProviderMatchAttributeTextInput,
    samlProviderMetaDataTextDescriptionStyle,
    samlProviderTextDescriptionStyle,
    samlProviderTextTitleStyle,
    samlTableStyle,
    samlTableTopPadding
} from '../styles/SAMLAuthenticationProviderStyle';


export interface SAMLProviderProps {
    provider: ISAMLAuthenticationProvider;
    samlMetaDataFile?: File;
    changesArePresent?: boolean;
    updateSamlProvider?: () => void;
    updateEmbeddedOption?: (allow: boolean) => void;
    updateAllowFullName?: (allow: boolean) => void;
    updateFirstNameMapping?: (name: string) => void;
    updateLastNameMapping?: (name: string) => void;
    updateFullNameMapping?: (name: string) => void;
    updateEmailMapping?: (email: string) => void;
    updateEmailAddressMapping?: (email: string) => void;
    updateSAMLMetadataFile?: (file: File, identityProviderEntityId: string, ssoServiceURL: string) => void;
}

export interface SAMLProviderState {
    downloadUrl: (evt: React.MouseEvent, url: string) => void;
    downloadLog: (evt: React.MouseEvent) => void;
    isUsable: () => boolean;
    removeSAMLMetadata: () => void;
    samlMetadataFile: File;
    setSAMLAttributeMapping: () => void;
    strings: _.Dictionary<string>;
    test: () => void;
    uploadSAMLMetadata: () => void;
    extractSAMLMetadataFields: (file: File) => void;
    userAttributes: IUserAttributes;
    isValidUsername: (username: string) => boolean;
    fileInputProps: FileInputProps;
    embeddedOptionRadioButtonIndex: number;
    fullNameRadioButtonIndex: number;
    renderMetadataFields: () => boolean;
}

interface IUserAttributes extends _.Dictionary<string> {}

const downloadLogEndpoint = 'siteSAMLLog';

export class SAMLAuthenticationProvider extends React.Component<SAMLProviderProps, SAMLProviderState> {
    private messageListener: (evt: MessageEvent) => void;

    constructor(props: SAMLProviderProps) {
        super(props);
        this.state = {
            downloadUrl: (evt: React.MouseEvent<HTMLButtonElement>, url: string) => {
                evt.preventDefault();
                WindowLocationService.download(url);
            },
            downloadLog: (evt: React.MouseEvent<HTMLButtonElement>) => {
                this.state.downloadUrl(evt, WebHttpClient.FILE_UPLOAD_API_PREFIX + downloadLogEndpoint);
            },
            isUsable: () => {
                return !!(this.props.provider.identityProviderEntityId);
            },
            removeSAMLMetadata: () => {
                if (!FeatureFlags.adminLockoutEnabled) {
                    this.handleUpdate(SiteAuthenticationSettingsService.removeSAMLMetadata(this.props.provider.id, this.props.provider.idpConfigurationId))
                    .then((resolved) => {
                        this.props.updateSamlProvider();
                    });
                } else {
                    this.props.updateSAMLMetadataFile(null, '', '');
                }
            },
            samlMetadataFile: null,
            setSAMLAttributeMapping: () => {
                this.handleUpdate(SiteAuthenticationSettingsService.setSAMLAttributeMapping(this.props.provider.id, this.props.provider.attributeMapping, this.props.provider.idpConfigurationId, this.props.provider.idpConfigurationName))
                  .then((resolved) => ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteAuthenticationAttributesSaved()}));
            },
            strings: this.strings,
            test: () => {
                WindowLocationService.open(this.props.provider.testLoginUrl,
                  'testLogin', { left: 20, top: 20, width: 500, height: 600, toolbar: 1, resizable: 1 });
            },
            uploadSAMLMetadata: () => {
                this.handleUpdate(SiteAuthenticationSettingsService.uploadSAMLMetadata(this.props.provider.id, this.state.samlMetadataFile))
                  .then((resolved) => {
                    this.props.updateSamlProvider();
                    ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteAuthenticationIdPImportSuccess()});
                  });
            },
            extractSAMLMetadataFields: (file: File) => {
                SiteAuthenticationSettingsService.extractSAMLMetadataXml(file)
                    .then((resolved: IIdPMetadataFields) => {
                        this.props.updateSAMLMetadataFile(file, resolved.identityProviderEntityId, resolved.ssoServiceURL);
                    }).catch(() => {
                        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.siteAuthenticationIdPImportFailureInvalidMetadata()});
                    });
            },
            userAttributes: null,
            isValidUsername: ServerSettingsUnauthenticated.isTableauIdSupported() ? EmailValidationHelper.isValidEmail : this.notBlank,
            fileInputProps: {
                fileName: 'entity_descriptor',
                testId:'idp-metadata-file-input',
                handleFileInput: this.handleFileInput
            },
            embeddedOptionRadioButtonIndex: this.props.provider.allowEmbeddedAuthentication ? 1 : 0,
            fullNameRadioButtonIndex: this.props.provider.attributeMapping.useFullName ? 1 : 0,
            renderMetadataFields: () => {
                return (!this.props.samlMetaDataFile && (this.props.provider.identityProviderEntityId && this.props.provider.ssoServiceURL)) && (!this.props.provider.isEnabled || this.props.changesArePresent);
            }
        };

        this.messageListener = (evt: MessageEvent) => this.receiveMessage(evt);
    }

    public componentDidMount() {
        const baseDomain = this.getBaseDomain(WindowLocationService.getOrigin());
        const testLoginBaseDomain = this.getBaseDomain(this.props.provider.testLoginUrl);

        // Enable cross-origin access to this window from the new window, provided they share the same base domain
        if (baseDomain === testLoginBaseDomain) {
            // Test login window is expected to postMessage bearing resolved UserDetails
            window.addEventListener('message', this.messageListener);
            if (!this.isRunningUnderTest()) {
                document.domain = baseDomain;
            }
        }
    }

    public componentWillUnmount() {
        // Unregister message listener on unmount to prevent memory leak
        window.removeEventListener('message', this.messageListener);
    }

    private translateValues = {
        SERVER_LEVEL_AUTH: AuthenticationTypeResolutionService.siteAuthDefaultLabel(),
        SERVER_NAME: ServerSettingsUnauthenticated.serverName()
    };

    private strings = {
        siteAuthenticationType:                 ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.siteAuthenticationType(): Messages.siteAuthenticationTypeNonTableauIDDesc(),
        defaultSiteAuthenticationType:          ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.siteAuthenticationTypeTableau(): Messages.siteAuthenticationServerLevelDefaultAuth(this.translateValues),
        siteAuthenticationStep1:                Messages.siteAuthenticationStep1(this.translateValues),
        siteAuthenticationExportFromSpOption2:  FeatureFlags.adminLockoutEnabled ? Messages.siteAuthenticationExportFromSpOption3(this.translateValues) : Messages.siteAuthenticationExportFromSpOption2(this.translateValues),
        siteAuthenticationEntityId:             Messages.siteAuthenticationEntityId(this.translateValues),
        siteAuthenticationImportToIdP:          Messages.siteAuthenticationImportToIdP2(this.translateValues),
        siteAuthenticationExportMetadataFromIdP:Messages.siteAuthenticationExportMetadataFromIdP2(this.translateValues),
        siteAuthenticationStep4:                Messages.siteAuthenticationStep4Title(),
        siteAuthenticationStep5Desc:            Messages.siteAuthenticationStep5Desc2(),
        siteAuthenticationTableauAttribute:     Messages.siteAuthenticationTableauAttribute(this.translateValues),
        usernameMapping:                        ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.username() : Messages.usernameOrEmail(),
        usernameMappingDescription:             Messages.siteAuthenticationUsernamesMustMatch(this.translateValues),
        siteAuthenticationNameAssertionsDesc:   Messages.siteAuthenticationNameAssertionsDesc2(this.translateValues),
        siteAuthenticationTroubleshootOption1:  Messages.siteAuthenticationTroubleshootOption1(),
        siteAuthenticationTroubleshootOption2:  Messages.siteAuthenticationTroubleshootOption2(this.translateValues),
        siteAuthenticationTroubleshootOption3:  ServerSettingsUnauthenticated.isTableauIdSupported() ? Messages.siteAuthenticationTroubleshootOption3() : Messages.siteAuthenticationTroubleshootOption3NonTableauID(),
        emailAddressMapping:                    Messages.emailAddress(),
        emailAddressMappingDescription:         Messages.siteAuthenticationSAMLEmailAddressMappingDesc()
    };

    private handleFileInput = (file: File) => {
        if (FeatureFlags.adminLockoutEnabled) {
            this.state.extractSAMLMetadataFields(file);
        } else {
            this.setState({samlMetadataFile: file});
        }
    };

    private notBlank(s: string): boolean {
        return s && /\S+/.test(s);
    }

    private handleUpdate(promise: Promise<any>): Promise<any> {
        promise.catch((reason: number) => {
          let translateKey = Messages.unexpectedError();
          if (reason === CommonErrorCode.InsecureSAMLEndpoint) {
            translateKey = Messages.siteAuthenticationIdPImportFailureInsecureEndpoint(this.translateValues);
          } else if (reason === CommonErrorCode.InvalidSAMLIdpMetadata) {
            translateKey = Messages.siteAuthenticationIdPImportFailureInvalidMetadata();
          }
          ToasterService.instance.pop({ type: ToastType.Error, text: translateKey});
        });
        return promise;
    }

    private setUserAttributes(userAttributes: IUserAttributes) {
        this.setState({userAttributes: userAttributes});
    }

    private getHostName(url: string): string {
        // Can't use URL since it's not supported in IE (or node)
        const urlParsingNode = document.createElement('a');
        urlParsingNode.setAttribute('href', url);
        return urlParsingNode.hostname;
    }

    private getBaseDomain(url: string): string {
        const hostName = this.getHostName(url);
        // If the host name is ip address, returns the ip address as the domain.
        if (this.isIPV4(hostName)) {
            return hostName;
        }

        const splitHost = hostName.split('.');
        const tldregex = new RegExp('(co|com|gov|gouv|net|org|edu|mil)');
        if (splitHost.length>=3 && tldregex.test(splitHost[splitHost.length-2])) {
            return splitHost.slice(-3).join('.');
        }
        else {
            return splitHost.slice(-2).join('.');
        }
    }

    private isIPV4(host: string): boolean {
        const elements = host.split('.');
        if (elements.length === 4) {
            return elements.every((element) => {
                const value = parseInt(element, 10);
                return !isNaN(value) && value >=0 && value <= 255;
            });
        }

        return false;
    }

    private isRunningUnderTest(): boolean {
        return window.hasOwnProperty('mocha');
    }

    public receiveMessage(evt: MessageEvent) {
        if (this.getHostName(evt.origin) !== this.getHostName(this.props.provider.testLoginUrl) && !this.isRunningUnderTest()) {
          return;
        }
        const userAttributes: IUserAttributes = JSON.parse(evt.data);
        this.setUserAttributes(userAttributes);
        if (!this.isRunningUnderTest()) {
          // (TFS 700140) This line causes the Karma debug window to close, when run via the unit tests
          (evt.source as any).close();
        }
    }

    private renderStep1(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">1</span>
                    {this.strings.siteAuthenticationStep1}
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {Messages.siteAuthenticationExportFromSpOptions()}
                        <div className="tb-auth-options-list">
                            &nbsp;
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                {Messages.siteAuthenticationExportFromSpOption1()}
                                &nbsp;
                                &nbsp;
                                <ButtonWidget
                                buttonType={ButtonType.Outline}
                                testId={'export-metadata-button'}
                                onClick={(evt) => {this.state.downloadUrl(evt, this.props.provider.serviceProviderEntityId);}}>
                                    {Messages.siteAuthenticationExportSpMetadata()}
                                </ButtonWidget>
                            </div>
                            {Messages.or()}
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                <span>
                                    {this.strings.siteAuthenticationExportFromSpOption2}
                                </span>
                            </div>
                        </div>
                        <div className="tb-auth-options-row">
                            <div className="tb-auth-options-label">
                                {this.strings.siteAuthenticationEntityId}
                            </div>
                            <TextFieldWidget
                            testId='server-entity-id-input'
                            label={''}
                            value={this.props.provider.serviceProviderEntityId}
                            readOnly={true}
                            size={35}
                            />
                        </div>
                        <div className="tb-auth-options-row">
                            <div className="tb-auth-options-label">
                                {Messages.siteAuthenticationAcsUrl()}
                            </div>
                            <TextFieldWidget
                            testId='assertion-consumer-serviceurl-input'
                            label={''}
                            value={this.props.provider.acsUrl}
                            readOnly={true}
                            size={35}
                            />
                        </div>
                        <div className="tb-auth-options-row">
                            <ButtonWidget
                            className="tb-outline-button.tb-align-right"
                            buttonType={ButtonType.Outline}
                            onClick={(evt) => {this.state.downloadUrl(evt, this.props.provider.certificateUrl);}}
                            testId="download-certificate-button">
                                {Messages.siteAuthenticationDownloadCertificate()}
                            </ButtonWidget>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Import metadata to IDP
    private renderStep2(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">2</span>
                    <span style={samlProviderDarkHighlightTextStyle}>
                        {Messages.siteAuthenticationExternalStep()}
                        &nbsp;
                    </span>
                    <span>
                        {Messages.siteAuthenticationStep2()}
                    </span>
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {this.strings.siteAuthenticationImportToIdP}
                    </div>
                </div>
            </div>
        );
    }

    // Export metadata to IDP
    private renderStep3(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">3</span>
                    <span style={samlProviderDarkHighlightTextStyle}>
                        {Messages.siteAuthenticationExternalStep()}
                        &nbsp;
                    </span>
                    <span>
                        {Messages.siteAuthenticationStep3()}
                    </span>
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {this.strings.siteAuthenticationExportMetadataFromIdP}
                    </div>
                </div>
            </div>
        );
    }

    // Import metadata to Tableau Cloud
    private renderStep4(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">4</span>
                    <span>{this.strings.siteAuthenticationStep4}</span>
                </div>
                <div className="tb-group-content">
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationIdPMetadataFile()}
                            </div>
                        </div>
                        <FileInput
                        fileUploadId={this.state.fileInputProps.fileUploadId}
                        fileName={this.state.fileInputProps.fileName}
                        handleFileInput={this.state.fileInputProps.handleFileInput}
                        testId='import-saml-file'/>
                        <ButtonWidget
                        className="tb-align-right"
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.samlMetadataFile}
                        onClick={this.state.uploadSAMLMetadata}
                        testId="idp-apply-metadata-button">
                            {Messages.apply()}
                        </ButtonWidget>
                    </div>
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationIdPEntityId()}
                            </div>
                        </div>
                        <TextFieldWidget
                            testId='input-provider-entity-id'
                            label={''}
                            key={this.props.provider.identityProviderEntityId}
                            value={this.props.provider.identityProviderEntityId}
                            readOnly={true}
                            size={35}
                            disabled={!this.state.isUsable()}
                        />
                    </div>
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationSSOServiceURL()}
                            </div>
                        </div>
                        <TextFieldWidget
                            testId='input-provider-sso'
                            label={''}
                            key={this.props.provider.ssoServiceURL}
                            value={this.props.provider.ssoServiceURL}
                            readOnly={true}
                            size={35}
                            disabled={!this.state.isUsable()}
                        />
                    </div>
                    <div style={samlProviderTextDescriptionStyle}>
                        {this.state.isUsable() && this.props.provider.isLogoutSupported ? Messages.siteAuthenticationIdPSupportsSingleLogout() : null}
                        {this.state.isUsable() && !this.props.provider.isLogoutSupported ? Messages.siteAuthenticationIdPDoesNotSupportSingleLogout() : null}
                    </div>
                    <div className="tb-auth-options-right-align">
                        <ButtonWidget
                        className="tb-float-left"
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.isUsable()}
                        onClick={this.state.test}
                        testId="idp-test-connection-button">
                            {Messages.siteAuthenticationTestLogin()}
                        </ButtonWidget>
                        <ButtonWidget
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.isUsable()}
                        onClick={this.state.removeSAMLMetadata}
                        testId="idp-remove-button">
                            {Messages.siteAuthenticationRemoveIdentityProvider()}
                        </ButtonWidget>
                    </div>
                    {this.state.userAttributes ?
                        <div className="tb-enabled-color">
                            {Messages.testConnectionSuccess()}
                        </div>
                    : null}
                </div>
            </div>
        );
    }

    // Match assertions
    private renderStep5(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className={this.state.isUsable() ? '' : 'tb-disabled'}>
                    <div style={samlProviderTextDescriptionStyle}>
                        <div className="tb-group-name">
                                <span className="tb-group-number">5</span>
                                <span>{Messages.siteAuthenticationStep5()}</span>
                        </div>
                        <div className="tb-group-content">
                            {this.strings.siteAuthenticationStep5Desc}
                            <div style={samlTableTopPadding}>
                                <div className="tb-auth-assertions">
                                    <span className="tb-auth-table-header">
                                        <span className="tb-auth-table-header-left">
                                            {this.strings.siteAuthenticationTableauAttribute}
                                        </span>
                                        <span className="tb-auth-table-header-right">
                                            {Messages.siteAuthenticationIdPName()}
                                        </span>
                                    </span>
                                    {this.renderStep5Tables()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderStep5Tables(): JSX.Element {
        const allowFullNameButtons = [
            {
                label: 'First and last name',
                testId: 'idp-attribute-usefirstlastname-radiobutton',
                value: false
            },
            {
                label: 'Full name',
                testId: 'idp-attribute-usefullname-radiobutton',
                value: true
            }
        ];
        const handleChange = (allow: boolean) => {
            this.props.updateAllowFullName(allow);
            this.setState({fullNameRadioButtonIndex: allow? 1 : 0});
        };
        const canDecoupleEmail = FeatureFlags.usernameEmailDecouplingEnabled;
        let attributesList = this.state.userAttributes && Object.entries(this.state.userAttributes).map(([attrName, attrVal]) => {
                return (
                    <option key={attrName} value={attrName}>{attrName + ' (' + attrVal + ')'}</option>
                );
        }, this);

        return (
            <div style={samlTableStyle}>
                <table>
                    <tbody>
                        <tr>
                            <td colSpan={2}>{this.strings.usernameMapping}</td>
                        </tr>
                        <tr>
                            <td>{this.strings.usernameMappingDescription}</td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-email-mapping-dropdown' onBlur={(evt) => this.props.updateEmailMapping(evt.target.value)}
                                    style={!this.state.isUsable() ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <div style={emailMappingTextBoxStyle}>
                                    <TextFieldWidget
                                        onBlur={(evt) => this.props.updateEmailMapping(evt.target.value)}
                                        testId='idp-attribute-assertion-textbox'
                                        label={''}
                                        disabled={!this.state.isUsable()}
                                        defaultValue={this.props.provider.attributeMapping.emailMapping}>
                                    </TextFieldWidget>
                                </div>}
                            </td>
                        </tr>
                    </tbody>
                </table>
                { (canDecoupleEmail) &&
                <table>
                    <tbody>
                        <tr>
                            <td colSpan={2}>{this.strings.emailAddressMapping}</td>
                        </tr>
                        <tr>
                            <td>{this.strings.emailAddressMappingDescription}</td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-email-address-mapping-dropdown' onBlur={(evt) => this.props.updateEmailAddressMapping(evt.target.value)}
                                    style={!this.state.isUsable() ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <div style={emailMappingTextBoxStyle}>
                                    <TextFieldWidget
                                        onBlur={(evt) => this.props.updateEmailAddressMapping(evt.target.value)}
                                        testId='idp-attribute-email-textbox'
                                        label={''}
                                        disabled={!this.state.isUsable()}
                                        defaultValue={this.props.provider.attributeMapping.emailAddressMapping ?? ''}
                                        placeholder=''>
                                    </TextFieldWidget>
                                </div>}
                            </td>
                        </tr>
                    </tbody>
                </table>}
                <table>
                    <tbody>
                        <tr>
                            <td colSpan={3}>{Messages.siteAuthenticationDisplayName()}</td>
                        </tr>
                        <tr>
                            <td colSpan={3}>{this.strings.siteAuthenticationNameAssertionsDesc}</td>
                        </tr>
                        <tr>
                            <td colSpan={3} className="tb-auth-assertions-radio">
                                <div id="full-name-radio-button" style={this.state.isUsable() ? null : samlDisabled}>
                                    <RadioButtonWithLabelGroupWidget
                                        aria-label={Messages.siteAuthenticationDisplayName()}
                                        className={'tb-dialog-authentication-option'}
                                        testId="allow-fullname-radio-buttons"
                                        buttonList={allowFullNameButtons}
                                        checkedIndex={this.state.fullNameRadioButtonIndex}
                                        handleChange={handleChange}
                                        layout={RadioLayoutType.Horizontal}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr className="tb-auth-assertion-grouping">
                            <td>&nbsp;</td>
                            <td className="tb-auth-assertions-radio-label">
                                {Messages.siteAuthenticationFirstName()}
                            </td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-firstname-mapping-dropdown' onBlur={(evt) => this.props.updateFirstNameMapping(evt.target.value)}
                                    style={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <TextFieldWidget
                                    onBlur={(evt) => this.props.updateFirstNameMapping(evt.target.value)}
                                    testId='idp-attribute-firstname-textbox'
                                    label={''}
                                    disabled={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName}
                                    defaultValue={this.props.provider.attributeMapping.firstNameMapping}>
                                </TextFieldWidget>}
                            </td>
                        </tr>
                        <tr className="tb-auth-assertion-grouping">
                            <td>&nbsp;</td>
                            <td className="tb-auth-assertions-radio-label">
                                {Messages.siteAuthenticationLastName()}
                            </td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-lastname-mapping-dropdown' onBlur={(evt) => this.props.updateLastNameMapping(evt.target.value)}
                                    style={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <TextFieldWidget
                                    onBlur={(evt) => this.props.updateLastNameMapping(evt.target.value)}
                                    testId='idp-attribute-lastname-textbox'
                                    label={''}
                                    disabled={!this.state.isUsable() || this.props.provider.attributeMapping.useFullName}
                                    defaultValue={this.props.provider.attributeMapping.lastNameMapping}>
                                </TextFieldWidget>}
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={3}></td>
                        </tr>
                        <tr className="tb-auth-assertion-grouping">
                            <td className="tb-auth-assertions-radio">
                                &nbsp;
                            </td>
                            <td className="tb-auth-assertions-radio-label">
                                {Messages.siteAuthenticationFullName()}
                            </td>
                            <td>
                                {this.state.userAttributes
                                ? <select id='idp-fullname-mapping-dropdown' onBlur={(evt) => this.props.updateFullNameMapping(evt.target.value)}
                                    style={!this.state.isUsable() || !this.props.provider.attributeMapping.useFullName ? samlDisabled : null}>
                                    {attributesList}
                                </select>
                                : <TextFieldWidget
                                    onBlur={(evt) => this.props.updateFullNameMapping(evt.target.value)}
                                    testId='idp-attribute-fullname-textbox'
                                    label={''}
                                    disabled={!this.state.isUsable() || !this.props.provider.attributeMapping.useFullName}
                                    defaultValue={this.props.provider.attributeMapping.fullNameMapping}>
                                </TextFieldWidget>}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className="tb-auth-options-right-align">
                    <ButtonWidget
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.isUsable()}
                        onClick={this.state.setSAMLAttributeMapping}
                        testId="idp-apply-attribute-button">
                            {Messages.apply()}
                    </ButtonWidget>
                </div>
            </div>
        );
    }

    // Embedding options
    private renderStep6(): JSX.Element {
        const embeddedOptionsButtons = [
            {
                label: Messages.siteAuthenticationEmbeddingDenied(),
                testId: 'authenticate-embedded-viz-popup-button',
                value: false
            },
            {
                label: Messages.siteAuthenticationEmbeddingAllowed(),
                testId: 'authenticate-embedded-viz-iframe-button',
                value: true
            }
        ];
        const handleChange = (allow: boolean) => {
            this.props.updateEmbeddedOption(allow);
            this.setState({embeddedOptionRadioButtonIndex: allow? 1 : 0});
        };

        return (
            <div className="tb-settings-group">
                <div style={samlProviderTextDescriptionStyle}>
                    <div className={this.state.isUsable() ? '' : 'tb-disabled'}>
                        <div className="tb-group-name">
                            <span className="tb-group-number">6</span>
                            <span>{Messages.ssoAuthEmbeddingOptions()}</span>
                        </div>
                        <div className="tb-group-content">
                            {Messages.ssoAuthEmbeddingOptionsDesc()}
                        </div>
                        <div className="tb-group-content">
                            <div style={this.state.isUsable() ? null : samlDisabled}>
                                <RadioButtonWithLabelGroupWidget
                                    aria-label={Messages.ssoAuthEmbeddingOptionsDesc()}
                                    className={'tb-dialog-authentication-option'}
                                    testId="embedded-option-radio-buttons"
                                    buttonList={embeddedOptionsButtons}
                                    checkedIndex={this.props.provider.allowEmbeddedAuthentication ? 1 : 0}
                                    handleChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Troubleshooting
    private renderStep7(): JSX.Element {
        return (
            <div className="tb-settings-group">
                <div className="tb-group-name">
                    <span className="tb-group-number">7</span>
                    <span>{Messages.siteAuthenticationStep7()}</span>
                </div>
                <div className="tb-group-content">
                    <div style={samlProviderTextDescriptionStyle}>
                        {Messages.siteAuthenticationTroubleshootActions()}
                        <div className="tb-auth-options-list">
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                {Messages.siteAuthenticationTroubleshootAssertionMatching()}
                                &nbsp;
                                <ButtonWidget
                                buttonType={ButtonType.Outline}
                                onClick={(evt) => this.state.downloadLog(evt)}
                                testId="download-troubleshooting-log-button">
                                    {Messages.siteAuthenticationDownloadLog()}
                                </ButtonWidget>
                            </div>
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                <span>{this.strings.siteAuthenticationTroubleshootOption1}</span>
                            </div>
                            <div className="tb-auth-options-list-item">
                                &#x25cf;
                                &nbsp;
                                <span>{this.strings.siteAuthenticationTroubleshootOption2}</span>
                            </div>
                        </div>
                    </div>
                    <span style={samlProviderTextDescriptionStyle}>{this.strings.siteAuthenticationTroubleshootOption3}</span>
                </div>
            </div>
        );
    }


    // Export metadata to IDP
    private renderStepExportMetadataToIDP(stepNumber: number = 3): JSX.Element {
        return (
            <div style={samlProviderGroupSettings}>
                <div style={samlProviderTextTitleStyle}>
                    <span style={samlProviderGroupNumber}>{stepNumber}.</span>
                    &nbsp;
                    <span>
                        {Messages.siteAuthenticationStep3Title()}
                    </span>
                </div>
                <div>
                    <div style={samlProviderTextDescriptionStyle}>
                        {this.strings.siteAuthenticationExportMetadataFromIdP}
                    </div>
                </div>
            </div>
        );
    }

    // Import metadata to Tableau Cloud
    private renderStepImportMetadataToTableauCloud(stepNumber: number = 4): JSX.Element {
        return (
            <div style={samlProviderGroupSettings}>
                <div style={samlProviderTextTitleStyle}>
                    <span>{stepNumber}.</span>
                    &nbsp;
                    <span>{this.strings.siteAuthenticationStep4}</span>
                </div>
                <div style={samlProviderTextDescriptionStyle}>
                        {FeatureFlags.featureFlagForMultipleIDPSupportEnabled
                            ? Messages.siteAuthenticationStepImportMetadataToTableauCloud()
                            : Messages.siteAuthenticationStep4Desc()}
                </div>
                <div>
                    <div>
                        <div>
                            <div style={{...samlProviderMetaDataTextDescriptionStyle, paddingTop: '8px', paddingBottom: '8px'}}>
                                {Messages.siteAuthenticationIdPMetadataFile()}
                            </div>
                        </div>
                        {
                            <AuthenticationFileInput
                            fileUploadId={this.state.fileInputProps.fileUploadId}
                            fileName={this.props.samlMetaDataFile?.name}
                            handleFileInput={this.state.fileInputProps.handleFileInput}
                            testId='import-saml-file'/>
                        }
                    </div>
                    {this.state.renderMetadataFields() ?
                        <div style={{paddingBottom: '8px'}}>
                            <CompactInlineAlertWidget type={InlineAlertType.Info} testId='metadata-file-warning' style={{...samlProviderAlertText, width: '460px'}}>
                                <div>
                                    <div>{Messages.siteAuthenticationMetadataFileWarning()}</div>
                                </div>
                            </CompactInlineAlertWidget>
                        </div>
                    : null}
                    <div style={samlProviderGroupContent}>
                        <div>
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationIdPEntityId()}
                            </div>
                        </div>
                        <TextFieldWidget
                            testId='input-provider-entity-id'
                            label={''}
                            key={this.props.provider.identityProviderEntityId}
                            value={this.state.renderMetadataFields() ? '' : this.props.provider.identityProviderEntityId}
                            readOnly={true}
                            size={35}
                            style={samlProviderImportMetadataTextInput}
                            kind='line'
                        />
                    </div>
                    <div style={samlProviderGroupContent}>
                        <div>
                            <div style={samlProviderTextDescriptionStyle}>
                                {Messages.siteAuthenticationIdPSSOServiceURL()}
                            </div>
                        </div>
                        <TextFieldWidget
                            testId='input-provider-sso'
                            label={''}
                            key={this.props.provider.ssoServiceURL}
                            value={this.state.renderMetadataFields() ? '' : this.props.provider.ssoServiceURL}
                            readOnly={true}
                            size={35}
                            style={samlProviderImportMetadataTextInput}
                            kind='line'
                        />
                    </div>
                    <div className="tb-auth-options-left-align">
                        <ButtonWidget
                        buttonType={ButtonType.Outline}
                        disabled={!this.state.isUsable()}
                        onClick={this.state.removeSAMLMetadata}
                        testId="idp-remove-button">
                            {Messages.siteAuthenticationClearIdentityProvider()}
                        </ButtonWidget>
                    </div>
                </div>
            </div>
        );
    }

    // Match assertions
    private renderStepMatchAssertions(stepNumber: number = 5): JSX.Element {
        return (
            <div style={samlProviderGroupSettings}>
                <div>
                    <div style={samlProviderTextDescriptionStyle}>
                        <div style={samlProviderTextTitleStyle}>
                            <span>{stepNumber}.</span>
                            &nbsp;
                            <span>{Messages.siteAuthenticationStep5()}</span>
                        </div>
                        <div style={samlProviderGroupSettings}>{this.strings.siteAuthenticationStep5Desc}</div>
                        <div>
                            <div>
                                <div className="tb-auth-assertions">
                                    {this.renderStepMatchAssertionsTables()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderStepMatchAssertionsTables(): JSX.Element {
        const handleChange = (allow: boolean) => {
            this.props.updateAllowFullName(allow);
            this.setState({fullNameRadioButtonIndex: allow ? 1 : 0});
        };
        const allowFullNameButtons = [
            {
                label: Messages.siteAuthenticationFirstAndLastName(),
                testId: 'idp-attribute-usefirstlastname-radiobutton',
                detail: (
                    <div>
                        {!this.props.provider.attributeMapping.useFullName ?
                            <div style={samlProviderGroupButton}>
                                <div>
                                    {Messages.siteAuthenticationFirstName()}
                                </div>
                                <TextFieldWidget
                                    onBlur={(evt) => this.props.updateFirstNameMapping(evt.target.value)}
                                    testId='idp-attribute-firstname-textbox'
                                    label={''}
                                    value={this.props.provider.attributeMapping.firstNameMapping}
                                    onChange={(evt) => this.props.updateFirstNameMapping(evt.target.value)}
                                    style={samlProviderMatchAttributeTextInput}
                                    kind='line'>
                                </TextFieldWidget>
                                <div style={{ paddingTop: 12 }}>
                                    {Messages.siteAuthenticationLastName()}
                                </div>
                                <TextFieldWidget
                                    onBlur={(evt) => this.props.updateLastNameMapping(evt.target.value)}
                                    testId='idp-attribute-lastname-textbox'
                                    label={''}
                                    value={this.props.provider.attributeMapping.lastNameMapping}
                                    onChange={(evt) => this.props.updateLastNameMapping(evt.target.value)}
                                    style={samlProviderMatchAttributeTextInput}
                                    kind='line'>
                                </TextFieldWidget>
                            </div>
                        : null}
                    </div>
                ),
                value: false
            },
            {
                label: Messages.siteAuthenticationFullName(),
                testId: 'idp-attribute-usefullname-radiobutton',
                detail: (
                    <div>
                        {this.props.provider.attributeMapping.useFullName ?
                            <div style={samlProviderGroupButton}>
                                <div className="tb-auth-assertions-radio-label">
                                    {Messages.siteAuthenticationFullName2()}
                                </div>
                                <TextFieldWidget
                                    onBlur={(evt) => this.props.updateFullNameMapping(evt.target.value)}
                                    testId='idp-attribute-fullname-textbox'
                                    label={''}
                                    value={this.props.provider.attributeMapping.fullNameMapping}
                                    onChange={(evt) => this.props.updateFullNameMapping(evt.target.value)}
                                    style={samlProviderMatchAttributeTextInput}
                                    kind='line'>
                                </TextFieldWidget>
                            </div>
                        : null}
                    </div>
                ),
                value: true
            }
        ];
        const canDecoupleEmail = FeatureFlags.usernameEmailDecouplingEnabled;
        let attributesList = this.state.userAttributes && Object.entries(this.state.userAttributes).map(([attrName, attrVal]) => {
                return (
                    <option key={attrName} value={attrName}>{attrName + ' (' + attrVal + ')'}</option>
                );
        }, this);

        return (
            <div style={samlTableStyle}>
                <div style={samlProviderGroupContent}>
                    <div style={samlProviderGroupContent}>
                        <div style={samlProviderMatchAttributesTitleStyle}>
                            <div>{this.strings.usernameMapping}</div>
                        </div>
                        <div style={samlProviderTextDescriptionStyle}>{this.strings.usernameMappingDescription}</div>
                    </div>
                        {this.state.userAttributes
                        ? <select id='idp-email-mapping-dropdown' onBlur={(evt) => this.props.updateEmailMapping(evt.target.value)}>
                            {attributesList}
                        </select>
                        : <div>
                            <div style={{...samlProviderTextDescriptionStyle, paddingBottom: 0}}>
                                <div>{this.strings.usernameMapping}</div>
                            </div>
                            <TextFieldWidget
                                onBlur={(evt) => this.props.updateEmailMapping(evt.target.value)}
                                testId='idp-attribute-assertion-textbox'
                                label={''}
                                value={this.props.provider.attributeMapping.emailMapping}
                                onChange={(evt) => this.props.updateEmailMapping(evt.target.value)}
                                style={samlProviderImportMetadataTextInput}
                                kind='line'>
                            </TextFieldWidget>
                        </div>}
                </div>
                { (canDecoupleEmail) &&
                <div>
                    <div style={samlProviderGroupContent}>
                        <div style={samlProviderMatchAttributesTitleStyle}>
                            <div>{this.strings.emailAddressMapping}</div>
                        </div>
                        <div style={samlProviderTextDescriptionStyle}>{this.strings.emailAddressMappingDescription}</div>
                    </div>
                        {this.state.userAttributes
                        ? <select id='idp-email-address-mapping-dropdown' onBlur={(evt) => this.props.updateEmailAddressMapping(evt.target.value)}>
                            {attributesList}
                        </select>
                        : <div>
                            <div style={{...samlProviderTextDescriptionStyle, paddingBottom: 0}}>
                                <div>{this.strings.emailAddressMapping}</div>
                            </div>
                            <TextFieldWidget
                                onBlur={(evt) => this.props.updateEmailAddressMapping(evt.target.value)}
                                testId='idp-attribute-email-textbox'
                                label={''}
                                value={this.props.provider.attributeMapping.emailAddressMapping}
                                onChange={(evt) => this.props.updateEmailAddressMapping(evt.target.value)}
                                style={samlProviderImportMetadataTextInput}
                                defaultValue={this.props.provider.attributeMapping.emailAddressMapping ?? ''}
                                placeholder=''
                                kind='line'>
                            </TextFieldWidget>
                        </div>}
                </div>}
                <div>
                    <div>
                        <div style={samlProviderGroupContent}>
                            <div style={samlProviderMatchAttributesTitleStyle}>
                                <div>{Messages.siteAuthenticationDisplayName()}</div>
                            </div>
                            <div style={samlProviderTextDescriptionStyle}>
                                {this.strings.siteAuthenticationNameAssertionsDesc}
                            </div>
                        </div>
                        <div>
                            <div id="full-name-radio-button">
                                <RadioButtonWithLabelGroupWidget
                                    aria-label={Messages.siteAuthenticationDisplayName()}
                                    className={'tb-dialog-authentication-option'}
                                    testId="allow-fullname-radio-buttons"
                                    buttonList={allowFullNameButtons}
                                    checkedIndex={this.props.provider.attributeMapping.useFullName? 1 : 0}
                                    handleChange={handleChange}
                                    layout={RadioLayoutType.Horizontal}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Embedding options
    public renderStepEmbeddingOptions(stepNumber: number = 6): JSX.Element {
        const embeddedOptionsButtons = [
            {
                label: Messages.siteAuthenticationEmbeddingDenied(),
                testId: 'authenticate-embedded-viz-popup-button',
                value: false
            },
            {
                label: Messages.siteAuthenticationEmbeddingAllowed(),
                testId: 'authenticate-embedded-viz-iframe-button',
                value: true
            }
        ];
        const handleChange = (allow: boolean) => {
            this.props.updateEmbeddedOption(allow);
            this.setState({embeddedOptionRadioButtonIndex: allow? 1 : 0});
        };

        return (
            <div>
                <div style={samlProviderTextDescriptionStyle}>
                    <div className={this.state.isUsable() || FeatureFlags.adminLockoutEnabled ? '' : 'tb-disabled'}>
                        <div style={samlProviderTextTitleStyle}>
                            <span>{stepNumber}.</span>
                            &nbsp;
                            <span>{Messages.ssoAuthEmbeddingOptionsOptional()}</span>
                        </div>
                        <div style={samlProviderGroupContent}>
                            {Messages.siteOIDCConfigurationStep5Description()}
                        </div>
                        <div>
                            <div style={this.state.isUsable() || FeatureFlags.adminLockoutEnabled ? null : samlDisabled}>
                                <RadioButtonWithLabelGroupWidget
                                    aria-label={Messages.siteOIDCConfigurationStep5Description()}
                                    className={'tb-dialog-authentication-option'}
                                    testId="embedded-option-radio-buttons"
                                    buttonList={embeddedOptionsButtons}
                                    checkedIndex={FeatureFlags.adminLockoutEnabled ? (this.props.provider.allowEmbeddedAuthentication ? 1 : 0)
                                        : this.state.embeddedOptionRadioButtonIndex}
                                    handleChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public render(): JSX.Element {
        const samlAuthenticationSteps = new SAMLAuthenticationSteps(this.props.provider, this.state.downloadUrl);
        return (
            <div>
                {FeatureFlags.adminLockoutEnabled ?
                    <div>
                        <div style={{...samlProviderGroupSettings, ...samlProviderTextDescriptionStyle, paddingTop: '4px'}}>
                            {Messages.siteAuthenticationTypeSAMLDescription3()}
                        </div>
                        {!FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? samlAuthenticationSteps.renderStepGetMetadata(1) : null}
                        {!FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? samlAuthenticationSteps.renderStepImportMetadataToIDP(2) : null}
                        {this.renderStepExportMetadataToIDP(!FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? 3 : 1)}
                        {this.renderStepImportMetadataToTableauCloud(!FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? 4 : 2)}
                        {this.renderStepMatchAssertions(!FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? 5 : 3)}
                        {this.renderStepEmbeddingOptions(!FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? 6 : 4)}
                    </div>
                :
                <div className="tb-group-name">
                        &nbsp;
                        <div className="tb-settings-group">
                            {Messages.siteAuthenticationTypeSAMLDescription2()}
                        </div>
                        {this.renderStep1()}
                        {this.renderStep2()}
                        {this.renderStep3()}
                        {this.renderStep4()}
                        {this.renderStep5()}
                        {this.renderStep6()}
                        {this.renderStep7()}
                </div>}
            </div>
        );
    }
}

export class SAMLAuthenticationSteps {
    private provider: ISAMLAuthenticationProvider;
    private downloadUrl: (evt: React.MouseEvent, url: string) => void;

    constructor(provider: ISAMLAuthenticationProvider, downloadUrl: (evt: React.MouseEvent, url: string) => void) {
        this.provider = provider;
        this.downloadUrl = downloadUrl;
    }

    private translateValues = {
        SERVER_LEVEL_AUTH: AuthenticationTypeResolutionService.siteAuthDefaultLabel(),
        SERVER_NAME: ServerSettingsUnauthenticated.serverName()
    };

    // Get Metadata
    public renderStepGetMetadata(stepNumber: number = 1): JSX.Element {
        return (
            <div style={samlProviderGroupSettings}>
                <div style={samlProviderTextTitleStyle}>
                    <span style={samlProviderGroupNumber}>{stepNumber}.</span>
                    <span>{Messages.siteAuthenticationStep1(this.translateValues)}</span>
                </div>
                <div>
                    <div>
                        <span style={samlProviderTextDescriptionStyle}>{Messages.siteAuthenticationShareWithIdP()}</span>
                        <div style={{paddingLeft:'20px'}}>
                            <div>
                                <ul style={samlProviderListStyle}>
                                    <li style={samlProviderExportMetadataLabelStyle}>{Messages.siteAuthenticationExportSpMetadataLabel()}</li>
                                </ul>
                                <p></p>
                                <div style={samlProviderGroupContent}>{Messages.siteAuthenticationExportSpMetadataDesc()}</div>
                                <ButtonWidget
                                className="tb-outline-button.tb-align-right"
                                buttonType={ButtonType.Outline}
                                testId={'export-metadata-button'}
                                onClick={(evt) => {this.downloadUrl(evt, this.provider.serviceProviderEntityId);}}>
                                    {Messages.siteAuthenticationExportSpMetadata()}
                                </ButtonWidget>
                                <p></p>
                                <ul style={samlProviderListStyle}>
                                    <li style={samlProviderExportMetadataLabelStyle}>{Messages.siteAuthenticationExportSpMetadataLabel2()}</li>
                                </ul>
                                <div>{Messages.siteAuthenticationExportSpMetadataDesc2()}</div>
                                <p></p>
                            </div>
                            <div style={samlProviderGroupContent}>
                                <div style={samlProviderTextDescriptionStyle}>
                                        {Messages.siteAuthenticationEntityId(this.translateValues)}
                                    <TextFieldWidget
                                    testId='server-entity-id-input'
                                    label={''}
                                    value={this.provider.serviceProviderEntityId}
                                    readOnly={true}
                                    size={35}
                                    style={samlProviderExportMetadataTextInput}
                                    kind='line'
                                    />
                                </div>
                            </div>
                            <div style={samlProviderGroupContent}>
                                <div style={samlProviderTextDescriptionStyle}>
                                        {Messages.siteAuthenticationTableauCloudAcsUrl()}
                                    <TextFieldWidget
                                    testId='assertion-consumer-serviceurl-input'
                                    label={''}
                                    value= {this.provider.acsUrl}
                                    readOnly={true}
                                    size={35}
                                    style={samlProviderExportMetadataTextInput}
                                    kind='line'
                                    />
                                </div>
                            </div>
                            <div style={samlProviderGroupContent}>
                                <ButtonWidget
                                className="tb-outline-button.tb-align-right"
                                buttonType={ButtonType.Outline}
                                onClick={(evt) => {this.downloadUrl(evt, this.provider.certificateUrl);}}
                                testId="download-certificate-button">
                                    {Messages.siteAuthenticationDownloadCertificate()}
                                </ButtonWidget>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Import metadata to IDP
    public renderStepImportMetadataToIDP(stepNumber: number = 2): JSX.Element {
        return (
            <div style={samlProviderGroupSettings}>
                <div style={samlProviderTextTitleStyle}>
                    <span style={samlProviderGroupNumber}>{stepNumber}.</span>
                    &nbsp;
                    <span>
                        {Messages.siteAuthenticationStep2Title()}
                    </span>
                </div>
                <div>
                    <div style={samlProviderTextDescriptionStyle}>
                        {FeatureFlags.featureFlagForMultipleIDPSupportEnabled
                            ? Messages.siteAuthenticationImportToIdpWithMutlipleIdp(this.translateValues)
                            : Messages.siteAuthenticationImportToIdP2(this.translateValues)}
                    </div>
                </div>
            </div>
        );
    }
}
